import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, BarChart3, Clock, Filter, Layers,
  RefreshCw, Search, Shield, ShieldAlert, ShieldCheck, Star,
  TrendingDown, TrendingUp, XCircle, Zap, Info, ListOrdered,
  Target, History, Archive, ArchiveRestore, Combine, CheckSquare,
  Square,
} from 'lucide-react';
import RuleHistoryDrawer from '../components/bank-rules/RuleHistoryDrawer';
import BulkApprovalModal from '../components/bank-rules/BulkApprovalModal';
import MergeDialog from '../components/bank-rules/MergeDialog';


/**
 * Matching Intelligence dashboard (Wave 2-D-1).
 *
 * Three side-by-side tabs read-only:
 *   • Analytics  → per-rule performance + global KPIs
 *   • Health     → conflicts, stale, brittle, low-priority findings
 *   • Ranking    → rules sorted by perf + suggested priority deltas
 *
 * No write actions in this wave (those land in Wave 2-D-2).
 */


const TABS = [
  { id: 'analytics', label: 'تَحليلات', icon: BarChart3 },
  { id: 'health',    label: 'الصِّحَّة',  icon: Shield },
  { id: 'ranking',   label: 'الترتيب', icon: ListOrdered },
];


function pct(x) {
  if (x == null) return '—';
  return `${(x * 100).toFixed(0)}%`;
}


function KpiCard({ label, value, tone = 'slate', icon: Icon, sub }) {
  const tones = {
    slate:    'bg-slate-50  border-slate-200  text-slate-900',
    indigo:   'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald:  'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:    'bg-amber-50  border-amber-200  text-amber-900',
    red:      'bg-red-50    border-red-200    text-red-900',
    sky:      'bg-sky-50    border-sky-200    text-sky-900',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-semibold opacity-75">{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}


function SuccessChip({ rate, events }) {
  if (events === 0) {
    return <span className="text-[10px] text-slate-400">لم تُطلَق</span>;
  }
  if (rate == null) return <span className="text-[10px] text-slate-400">—</span>;
  const tone = rate >= 0.9  ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                rate >= 0.7  ? 'bg-sky-100 text-sky-800 border-sky-200' :
                rate >= 0.5  ? 'bg-amber-100 text-amber-800 border-amber-200' :
                               'bg-red-100 text-red-800 border-red-200';
  return (
    <span className={`inline-block text-[10px] font-mono font-semibold border rounded px-1.5 py-0.5 ${tone}`}>
      {(rate * 100).toFixed(0)}%
    </span>
  );
}


function PriorityDelta({ from, to }) {
  const d = to - from;
  if (Math.abs(d) <= 1) {
    return <span className="text-[10px] text-slate-400 font-mono">{from}</span>;
  }
  const up = d > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono font-semibold ${
      up ? 'text-emerald-700' : 'text-red-700'}`}>
      <span className="opacity-50">{from}</span>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      <span>{to}</span>
      <span className="text-[9px] opacity-60">({d > 0 ? '+' : ''}{d})</span>
    </span>
  );
}


// ── Analytics tab ────────────────────────────────────────────────


function AnalyticsTab({ days, setDays, q, setQ, onOpenHistory, onToggleSelect, selected, onArchive }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.get('/bank-rules-intel/analytics',
        { params: { days, q: q || undefined } });
      setData(data);
    } catch {
      toast.error('فَشَل تَحميل التَّحليلات');
    } finally { setBusy(false); }
  };

   
  useEffect(() => { load(); }, [days, q]);

  if (!data) {
    return <div className="text-center text-slate-400 py-10 text-sm">…تَحميل</div>;
  }
  const k = data.kpis || {};
  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard label="إجمالي القَواعِد"  value={k.total_rules ?? 0}    tone="slate"   icon={Layers} />
        <KpiCard label="مُفَعَّلَة"           value={k.enabled ?? 0}        tone="indigo"  icon={Zap} />
        <KpiCard label={`أُطلِقَت / ${k.window_days || 30}ي`} value={k.fired_in_window ?? 0} tone="sky" icon={Clock} />
        <KpiCard label="نَجاح إجمالي"      value={pct(k.global_success_rate)} tone="emerald" icon={ShieldCheck} />
        <KpiCard label="قَواعِد للمُراجَعَة" value={(k.brittle_count ?? 0) + (k.stale_count ?? 0)}
                  tone="amber" icon={AlertTriangle}
                  sub={`${k.brittle_count} هَشَّة · ${k.stale_count} خامِلَة · ${k.unused_count} غَير مُستَخدَمَة`} />
      </div>

      {data.top_performers?.length > 0 && (
        <div className="rounded-xl border bg-emerald-50/40 border-emerald-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-emerald-600" />
            <div className="text-sm font-bold text-emerald-900">أَفضَل القَواعِد أَداءً</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.top_performers.map((tp) => (
              <span key={tp.id} data-testid={`top-${tp.id}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-emerald-200 rounded-full px-3 py-1">
                <span className="font-semibold text-emerald-900">{tp.name}</span>
                <span className="font-mono text-emerald-700">{pct(tp.success_rate)}</span>
                <span className="text-[10px] text-slate-500">· {tp.total_events} حَدَث</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-start px-3 py-2 w-8"></th>
              <th className="text-start px-3 py-2">القاعِدَة</th>
              <th className="text-start px-3 py-2">النَّمط</th>
              <th className="text-start px-3 py-2">الهَدَف</th>
              <th className="text-end px-3 py-2">إطلاقات</th>
              <th className="text-end px-3 py-2">رَفض</th>
              <th className="text-end px-3 py-2">تَجاوُز</th>
              <th className="text-start px-3 py-2">نَجاح</th>
              <th className="text-start px-3 py-2">آخِر إطلاق</th>
              <th className="text-start px-3 py-2">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => {
              const isSel = selected?.has(r.id);
              return (
              <tr key={r.id} className={`border-t hover:bg-slate-50/50 ${
                !r.enabled ? 'opacity-50' : ''} ${r.archived ? 'bg-zinc-50/50' : ''}`}
                   data-testid={`row-${r.id}`}>
                <td className="px-3 py-2">
                  <button onClick={() => onToggleSelect?.(r.id)}
                          data-testid={`select-${r.id}`}
                          className="text-slate-500 hover:text-indigo-600">
                    {isSel ? <CheckSquare className="w-4 h-4 text-indigo-600" />
                            : <Square className="w-4 h-4" />}
                  </button>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-semibold">{r.name}{r.archived && ' 📦'}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    أَولَوِيَّة {r.priority}{!r.enabled && ' · مُعَطَّلَة'}{r.archived && ' · مُؤَرشَفَة'}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs font-mono text-slate-600">
                  <div className="truncate max-w-[180px]" title={r.pattern}>{r.pattern}</div>
                  <div className="text-[10px] text-slate-400">{r.pattern_type}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.action_value_label || <span className="text-slate-400">—</span>}
                  <div className="text-[10px] text-slate-400">{r.action_kind}</div>
                </td>
                <td className="px-3 py-2 text-end font-mono text-xs text-emerald-700">{r._match_count}</td>
                <td className="px-3 py-2 text-end font-mono text-xs text-red-700">{r._reject_count}</td>
                <td className="px-3 py-2 text-end font-mono text-xs text-amber-700">{r._override_count}</td>
                <td className="px-3 py-2"><SuccessChip rate={r._success_rate} events={r._total_events} /></td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">
                  {r._idle_days == null ? '—' :
                    r._idle_days === 0 ? 'اليوم' : `قَبل ${r._idle_days} يَوم`}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => onOpenHistory?.(r)}
                            data-testid={`history-btn-${r.id}`}
                            title="عَرض التاريخ"
                            className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-indigo-600">
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onArchive?.(r)}
                            data-testid={`archive-btn-${r.id}`}
                            title={r.archived ? 'فَك الأَرشَفَة' : 'أَرشَفَة'}
                            className="p-1 rounded hover:bg-slate-100 text-slate-600 hover:text-amber-600">
                      {r.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </td>
              </tr>
            );})}
            {data.items.length === 0 && (
              <tr><td colSpan={10} className="text-center text-slate-400 py-6 text-sm">
                لا تُوجَد قَواعِد تُطابِق الفِلتَر.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Health tab ───────────────────────────────────────────────────


function HealthTab() {
  const [data, setData] = useState(null);
  const [idleDays, setIdleDays] = useState(60);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const { data } = await axiosInstance.get('/bank-rules-intel/health',
        { params: { idle_days: idleDays } });
      setData(data);
    } catch {
      toast.error('فَشَل تَحميل الصِّحَّة');
    }
  };
   
  useEffect(() => { load(); }, [idleDays]);

  const items = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.items;
    return data.items.filter((f) => f.severity === filter);
  }, [data, filter]);

  if (!data) {
    return <div className="text-center text-slate-400 py-10 text-sm">…تَحميل</div>;
  }
  const scoreTone = data.health_score >= 90 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                     data.health_score >= 70 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                                                'text-red-700 bg-red-50 border-red-200';
  return (
    <div className="space-y-4" data-testid="health-tab">
      <div className="rounded-xl border bg-white p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-7 h-7 text-indigo-600" />
          <div>
            <div className="text-xs uppercase text-slate-500">نَتيجَة الصِّحَّة الإجماليَّة</div>
            <div className={`inline-block text-3xl font-bold font-mono border rounded-lg px-3 py-1 mt-1 ${scoreTone}`}
                  data-testid="health-score">
              {data.health_score}/100
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs bg-red-50 border border-red-200 text-red-800 rounded-full px-2 py-1">
            <XCircle className="w-3 h-3" /> حَرِجَة: {data.counts.critical}
          </span>
          <span className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2 py-1">
            <AlertTriangle className="w-3 h-3" /> تَحذير: {data.counts.warning}
          </span>
          <span className="inline-flex items-center gap-1 text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-full px-2 py-1">
            <Info className="w-3 h-3" /> مَعلومات: {data.counts.info}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">عَتَبَة الخُمول (يَوم):</label>
          <input type="number" min="7" max="365" value={idleDays}
                 onChange={(e) => setIdleDays(Number(e.target.value) || 60)}
                 data-testid="health-idle-days"
                 className="w-20 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {['all', 'critical', 'warning', 'info'].map((sv) => (
          <button key={sv} onClick={() => setFilter(sv)}
                  data-testid={`health-filter-${sv}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1 border ${
                    filter === sv ? 'bg-slate-900 text-white border-slate-900'
                                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
            {sv === 'all' ? 'الكُل' : sv === 'critical' ? 'حَرِجَة' : sv === 'warning' ? 'تَحذير' : 'مَعلومات'}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map((f, i) => {
          const Icon = f.severity === 'critical' ? XCircle :
                       f.severity === 'warning'  ? AlertTriangle : Info;
          const tone = f.severity === 'critical' ? 'bg-red-50 border-red-200 text-red-900' :
                       f.severity === 'warning'  ? 'bg-amber-50 border-amber-200 text-amber-900' :
                                                   'bg-sky-50 border-sky-200 text-sky-900';
          return (
            <div key={i} className={`rounded-xl border p-3 flex items-start gap-3 ${tone}`}
                 data-testid={`finding-${i}`}>
              <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono opacity-70">
                  {f.type}{f.rule_id ? ` · ${f.rule_id.slice(0, 8)}` : ''}
                </div>
                <div className="text-sm font-semibold mt-0.5">
                  {f.name || f.pattern || '—'}
                </div>
                <div className="text-xs mt-1 opacity-90">{f.message}</div>
                {f.shadowed_names?.length > 0 && (
                  <div className="text-[10px] mt-1 opacity-70 font-mono">
                    مُتَجاوَزات: {f.shadowed_names.join(' · ')}
                  </div>
                )}
                {f.success_rate != null && (
                  <div className="text-[10px] mt-1 opacity-70 font-mono">
                    نَجاح: {pct(f.success_rate)} · إطلاقات: {f.match_count}
                    {' · رَفض: '}{f.reject_count}{' · تَجاوُز: '}{f.override_count}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="text-center text-slate-400 py-8 text-sm flex items-center justify-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            لا تُوجَد مَلاحَظات تَحت هَذا التَّصنيف.
          </div>
        )}
      </div>
    </div>
  );
}


// ── Ranking tab ──────────────────────────────────────────────────


function RankingTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const { data } = await axiosInstance.get('/bank-rules-intel/ranking');
      setData(data);
    } catch { toast.error('فَشَل تَحميل التَّرتيب'); }
  };
  useEffect(() => { load(); }, []);

  const items = useMemo(() => {
    if (!data) return [];
    if (filter === 'up')    return data.items.filter((r) => r._priority_delta >  5);
    if (filter === 'down')  return data.items.filter((r) => r._priority_delta < -5);
    if (filter === 'stable') return data.items.filter((r) => Math.abs(r._priority_delta) <= 5);
    return data.items;
  }, [data, filter]);

  if (!data) {
    return <div className="text-center text-slate-400 py-10 text-sm">…تَحميل</div>;
  }
  return (
    <div className="space-y-4" data-testid="ranking-tab">
      <div className="grid grid-cols-3 gap-2">
        <KpiCard label="رَفع مُقتَرَح"      value={data.summary.moves_up}     tone="emerald" icon={TrendingUp} />
        <KpiCard label="خَفض مُقتَرَح"      value={data.summary.moves_down}   tone="red"     icon={TrendingDown} />
        <KpiCard label="بِدون تَغيير"      value={data.summary.unchanged}    tone="slate"   icon={Target} />
      </div>

      <div className="flex items-center gap-1.5">
        {[
          { id: 'all',    label: 'الكُل' },
          { id: 'up',     label: 'رَفع' },
          { id: 'down',   label: 'خَفض' },
          { id: 'stable', label: 'مُستَقِرّ' },
        ].map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)}
                  data-testid={`ranking-filter-${t.id}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1 border ${
                    filter === t.id ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <span className="text-[10px] text-slate-500">
          الاقتِراحات قابِلَة لِلتَّطبيق دُفعَة واحِدَة في الموجَة القادِمَة 2-D-2.
        </span>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-start px-3 py-2">#</th>
              <th className="text-start px-3 py-2">القاعِدَة</th>
              <th className="text-start px-3 py-2">الأَداء</th>
              <th className="text-start px-3 py-2">نَجاح</th>
              <th className="text-end px-3 py-2">أَحداث</th>
              <th className="text-start px-3 py-2">الأَولَوِيَّة المُقتَرَحَة</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, idx) => (
              <tr key={r.id} className={`border-t hover:bg-slate-50/50 ${
                !r.enabled ? 'opacity-50' : ''}`} data-testid={`rank-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{idx + 1}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {r.pattern_type}: <code>{r.pattern}</code>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500"
                            style={{ width: `${(r._perf * 100).toFixed(0)}%` }} />
                    </div>
                    <span className="text-[10px] font-mono text-slate-600">
                      {(r._perf * 100).toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2"><SuccessChip rate={r._success_rate} events={r._total_events} /></td>
                <td className="px-3 py-2 text-end font-mono text-xs">{r._total_events}</td>
                <td className="px-3 py-2">
                  <PriorityDelta from={r.priority} to={r._suggested_priority} />
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-6 text-sm">
                لا تُوجَد قَواعِد تُطابِق الفِلتَر.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Page shell ───────────────────────────────────────────────────


export default function MatchingIntelligence() {
  const [tab, setTab] = useState('analytics');
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [bumpKey, setBumpKey] = useState(0);
  const [historyRule, setHistoryRule] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkExplicitIds, setBulkExplicitIds] = useState([]);
  const [mergePair, setMergePair] = useState(null);   // {target, source}
  const [selectedIds, setSelectedIds] = useState(new Set());

  const refreshAll = () => { setBumpKey((k) => k + 1); toast.success('تَم التَّحديث'); };

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const archiveRule = async (rule) => {
    const action = rule.archived ? 'unarchive' : 'archive';
    if (!window.confirm(rule.archived ? `فَك أَرشَفَة «${rule.name}»؟`
                                       : `أَرشَفَة «${rule.name}»؟`)) return;
    try {
      await axiosInstance.post(`/bank-rules/${rule.id}/${action}`, {});
      toast.success(rule.archived ? 'تَم فَك الأَرشَفَة' : 'تَم الأَرشَفَة');
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فَشَل'); }
  };

  const openBulkForSelected = () => {
    setBulkExplicitIds(Array.from(selectedIds));
    setBulkOpen(true);
  };

  const openBulkGlobal = () => {
    setBulkExplicitIds([]);
    setBulkOpen(true);
  };

  const startMerge = () => {
    const ids = Array.from(selectedIds);
    if (ids.length !== 2) {
      toast.error('اِختَر قاعِدَتَين بِالضَّبط لِلدَّمج');
      return;
    }
    // We need the full rule docs — fetch from analytics endpoint quickly.
    axiosInstance.get('/bank-rules-intel/analytics').then(({ data }) => {
      const a = data.items.find((r) => r.id === ids[0]);
      const b = data.items.find((r) => r.id === ids[1]);
      if (!a || !b) { toast.error('لَم يَتِم العُثور على القَواعِد'); return; }
      // Heuristic: target = the one with more matches.
      const [tgt, src] = (a._match_count >= b._match_count) ? [a, b] : [b, a];
      setMergePair({ target: tgt, source: src });
    });
  };

  return (
    <div className="p-4 max-w-7xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            ذَكاء المُطابَقَة
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            تَحليلات + صِحَّة + تَرتيب + إصدارات لِكُل قَواعِد المُطابَقَة البَنكية.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1.5"
                 data-testid="selection-toolbar">
              <span className="text-xs text-indigo-700 font-semibold">
                {selectedIds.size} مُحَدَّدَة
              </span>
              <button onClick={openBulkForSelected}
                      data-testid="bulk-selected-btn"
                      className="text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1">
                اعتِماد جَماعي
              </button>
              {selectedIds.size === 2 && (
                <button onClick={startMerge}
                        data-testid="start-merge-btn"
                        className="inline-flex items-center gap-1 text-xs font-semibold rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-1">
                  <Combine className="w-3 h-3" /> دَمج
                </button>
              )}
              <button onClick={() => setSelectedIds(new Set())}
                      data-testid="clear-selection-btn"
                      className="text-xs text-indigo-700 hover:text-indigo-900 px-1">
                مَسح
              </button>
            </div>
          )}
          <button onClick={openBulkGlobal}
                  data-testid="bulk-global-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2">
            <CheckSquare className="w-4 h-4" /> اعتِماد جَماعي بِفِلاتِر
          </button>
          <button onClick={refreshAll}
                  data-testid="refresh-all-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2">
            <RefreshCw className="w-4 h-4" /> تَحديث
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-3 border-b">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 -mb-px border-b-2 transition ${
                      tab === t.id ? 'border-indigo-600 text-indigo-700'
                                   : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar (analytics only) */}
      {tab === 'analytics' && (
        <div className="flex items-center gap-2 flex-wrap mb-3 bg-slate-50 border border-slate-200 rounded-xl p-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="بَحث في القَواعِد…"
                   data-testid="analytics-search"
                   className="w-full rounded-lg border border-slate-300 px-3 py-1.5 ps-8 text-sm" />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-600">نافِذَة:</label>
            {[7, 30, 90, 180].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                      data-testid={`analytics-days-${d}`}
                      className={`text-xs font-mono rounded px-2 py-1 border ${
                        days === d ? 'bg-slate-900 text-white border-slate-900'
                                   : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                {d}ي
              </button>
            ))}
          </div>
        </div>
      )}

      <div key={bumpKey}>
        {tab === 'analytics' && (
          <AnalyticsTab
            days={days} setDays={setDays} q={q} setQ={setQ}
            onOpenHistory={setHistoryRule}
            onToggleSelect={toggleSelect}
            selected={selectedIds}
            onArchive={archiveRule}
          />
        )}
        {tab === 'health'    && <HealthTab />}
        {tab === 'ranking'   && <RankingTab />}
      </div>

      {historyRule && (
        <RuleHistoryDrawer
          rule={historyRule}
          onClose={() => setHistoryRule(null)}
          onMutated={refreshAll}
        />
      )}
      {bulkOpen && (
        <BulkApprovalModal
          initialRuleIds={bulkExplicitIds}
          onClose={() => setBulkOpen(false)}
          onApplied={() => { refreshAll(); setSelectedIds(new Set()); }}
        />
      )}
      {mergePair && (
        <MergeDialog
          target={mergePair.target}
          source={mergePair.source}
          onClose={() => setMergePair(null)}
          onMerged={() => { refreshAll(); setSelectedIds(new Set()); }}
        />
      )}
    </div>
  );
}
